import {useCallback, useEffect, useEffectEvent, useMemo, useRef, useState, useSyncExternalStore} from 'react'
import type {
  EstablishedAhpDirectTurnTelemetry,
  EstablishedAhpSession,
  EstablishedAhpSessionBootstrap,
  EstablishedAhpSessionIdentity,
  EstablishedAhpResumptionAttempt,
} from '@github-ui/ahp-session-client/contracts'
import {createMissionControlEstablishedAhpBootstrap} from '../ahp/established-ahp-bootstrap'
import {createAhpRelayDiagnostics} from '../ahp/ahp-debug-log'
import {AhpChatStore} from '@github-ui/ahp-relay/ahp-chat-store'
import type {RelayActiveClientRole, RelayCreateSessionOptions} from '@github-ui/ahp-relay/relay-connection-runtime'
import {
  discardRelayInstructionRulesWhenUnused,
  getRelayConnectionProgress,
  getRelayEntry,
  recordRelayRecoveryIntent,
  subscribeRelayRegistry,
  type RelayConnectionProgressSnapshot,
} from '@github-ui/ahp-relay/relay-registry'
import {InstructionRuleSessionPublisher} from '@github-ui/copilot-chat/utils/instruction-rules-publisher'
import type {InstructionScope} from '@github-ui/ahp-session-client/contributed-instructions'
import {mcFetcher} from '../utils/mc-fetcher'
import {relayFailureDisposition, relayFailureFromError, type RelayFailure} from '@github-ui/ahp-relay/relay-failure'
import {observeResultPresentation} from '@github-ui/ahp-relay/relay-result-presentation'

export interface UseEstablishedAhpSessionOptions {
  taskId: string | undefined
  environmentId: string | undefined
  sessionId: string | undefined
  channel?: string
  enabled: boolean
  wakeable?: boolean
  recoveryCreateSession?: RelayCreateSessionOptions
  instructionScope?: InstructionScope
  bootstrap?: EstablishedAhpSessionBootstrap
  /**
   * Marks the supplied bootstrap as the only acceptable one. Without this an
   * absent `bootstrap` silently falls back to Mission Control's default, which
   * for a surface that owns its own wiring means attaching through the wrong
   * runtime rather than not attaching at all.
   */
  requiresSuppliedBootstrap?: boolean
  /** The caller created this bootstrap for this hook, rather than lending it. */
  ownsSuppliedBootstrap?: boolean
  /** Existing-session attempt started by an earlier identity/metadata owner. */
  resumptionAttempt?: EstablishedAhpResumptionAttempt
  /** Page-level presentation owns the continuous slow-warning clock. */
  slowWarningEnabled?: boolean
  directTurnTelemetry?: EstablishedAhpDirectTurnTelemetry
  onSessionEstablished?: (session: EstablishedAhpSession) => void
  awaitIntentAfterLive?: boolean
  /**
   * `never` attaches as a plain subscriber: no instruction rules and no
   * `session/activeClientSet`, so another surface that owns the session keeps its role.
   */
  activeClientRole?: RelayActiveClientRole
}

export interface UseEstablishedAhpSessionResult {
  session: EstablishedAhpSession | undefined
  error: string | undefined
  failure?: RelayFailure
  connecting: boolean
  live: boolean
  reconnecting: boolean
  awaitingIntent: boolean
  progress?: RelayConnectionProgressSnapshot
  showProgress?: boolean
  presentationPhase?: 'waking' | 'connecting'
  slow?: boolean
  canRetry?: boolean
  attempt?: number
  connect: () => void
}

interface EstablishedAhpSessionState {
  key: string
  bootstrap?: EstablishedAhpSessionBootstrap
  session?: EstablishedAhpSession
  error?: string
  failure?: RelayFailure
  mode?: 'attach' | 'wake' | 'recover'
  attempt: number
}

const subscribeNothing = () => () => {}
const disconnected = () => 'connecting' as const

export function useConnectionAttemptPresentation(
  connecting: boolean,
  attemptKey: string,
  progress: RelayConnectionProgressSnapshot | undefined,
  slowWarningEnabled = true,
) {
  const reportedPhase =
    progress?.phase === 'waking-environment'
      ? 'waking'
      : progress && progress.phase !== 'contacting-environment' && progress.phase !== 'retrying'
        ? 'connecting'
        : undefined
  const [presentationState, setPresentationState] = useState<{
    connecting: boolean
    attemptKey: string
    attempt?: {attemptKey: string; phase: 'waking' | 'connecting'}
  }>({connecting: false, attemptKey: ''})
  let nextPresentationState = presentationState
  if (!connecting) {
    if (presentationState.connecting) nextPresentationState = {connecting: false, attemptKey: ''}
  } else if (!presentationState.connecting || presentationState.attemptKey !== attemptKey) {
    nextPresentationState = {
      connecting: true,
      attemptKey,
      ...(reportedPhase ? {attempt: {attemptKey, phase: reportedPhase}} : {}),
    }
  } else if (
    reportedPhase &&
    (!presentationState.attempt || (presentationState.attempt.phase === 'waking' && reportedPhase === 'connecting'))
  ) {
    nextPresentationState = {connecting: true, attemptKey, attempt: {attemptKey, phase: reportedPhase}}
  }
  if (nextPresentationState !== presentationState) setPresentationState(nextPresentationState)
  const activePresentationAttempt = nextPresentationState.attempt
  const [elapsedAttempt, setElapsedAttempt] = useState<object>()
  useEffect(() => {
    if (!activePresentationAttempt || !slowWarningEnabled) return
    const slowTimer = setTimeout(
      () => setElapsedAttempt(activePresentationAttempt),
      activePresentationAttempt.phase === 'waking' ? 10_000 : 5_000,
    )
    return () => {
      clearTimeout(slowTimer)
    }
  }, [activePresentationAttempt, slowWarningEnabled])

  return {
    showProgress: connecting,
    presentationPhase: activePresentationAttempt?.phase,
    slow: connecting && !!activePresentationAttempt && elapsedAttempt === activePresentationAttempt,
  }
}

function identityFor(options: UseEstablishedAhpSessionOptions): EstablishedAhpSessionIdentity | undefined {
  if (!options.taskId || !options.environmentId || !options.sessionId) return undefined
  return {
    taskId: options.taskId,
    environmentId: options.environmentId,
    sessionId: options.sessionId,
    ...(options.channel ? {channel: options.channel} : {}),
  }
}

function identitiesMatch(
  first: EstablishedAhpSessionIdentity | undefined,
  second: EstablishedAhpSessionIdentity,
): boolean {
  return (
    !!first &&
    first.taskId === second.taskId &&
    first.environmentId === second.environmentId &&
    first.sessionId === second.sessionId &&
    first.channel === second.channel
  )
}

/**
 * Creates one established-session handle for the active Mission Control root.
 * History and task eligibility stay in the caller; this hook only attaches or
 * wakes the already-resolved AHP identity. The default task-view path does not
 * claim the active-client role because it could displace a host-bound surface;
 * missing tools fail fast in the host and let the model fall back to prose.
 */
export function useEstablishedAhpSession({
  taskId,
  environmentId,
  sessionId,
  channel,
  enabled,
  wakeable = false,
  recoveryCreateSession,
  instructionScope = 'repository',
  bootstrap: suppliedBootstrap,
  requiresSuppliedBootstrap = false,
  ownsSuppliedBootstrap = false,
  resumptionAttempt,
  slowWarningEnabled = true,
  directTurnTelemetry,
  onSessionEstablished,
  awaitIntentAfterLive = false,
  activeClientRole = 'default',
}: UseEstablishedAhpSessionOptions): UseEstablishedAhpSessionResult {
  const notifySessionEstablished = useEffectEvent((session: EstablishedAhpSession) => {
    if (onSessionEstablished) observeResultPresentation(() => onSessionEstablished(session))
  })
  const identity = useMemo(
    () => identityFor({taskId, environmentId, sessionId, channel, enabled}),
    [channel, environmentId, enabled, sessionId, taskId],
  )
  const defaultBootstrap = useMemo(
    () =>
      createMissionControlEstablishedAhpBootstrap({
        provisionSession: async () => {
          throw new Error('Mission Control active-session bootstrap cannot provision a new task.')
        },
        createRelayOptions: (relayIdentity, request) => ({
          fetcher: mcFetcher,
          createSink: () => new AhpChatStore(),
          acceptInstructionRules: activeClientRole !== 'never',
          ...(activeClientRole === 'never' ? {activeClientRole} : {}),
          hostTools: {kind: 'none'},
          ...createAhpRelayDiagnostics(relayIdentity.taskId ?? relayIdentity.sessionId),
          ...(request?.mode === 'recover' && recoveryCreateSession ? {createSession: recoveryCreateSession} : {}),
        }),
        directTurnTelemetry,
      }),
    [activeClientRole, directTurnTelemetry, recoveryCreateSession],
  )
  const bootstrap = suppliedBootstrap ?? (requiresSuppliedBootstrap ? undefined : defaultBootstrap)
  const ownsBootstrap = !suppliedBootstrap || ownsSuppliedBootstrap
  const sessionKey =
    enabled && identity
      ? `${identity.taskId}\u0000${identity.environmentId}\u0000${identity.sessionId}\u0000${identity.channel ?? ''}`
      : ''
  const initialMode = sessionKey ? (recoveryCreateSession ? 'recover' : wakeable ? 'wake' : 'attach') : undefined
  const [state, setState] = useState<EstablishedAhpSessionState>({
    key: sessionKey,
    bootstrap,
    mode: initialMode,
    attempt: 0,
  })

  if (state.key !== sessionKey || state.bootstrap !== bootstrap) {
    setState({key: sessionKey, bootstrap, mode: initialMode, attempt: 0})
  }
  const mode = state.mode
  const attempt = state.attempt
  const previousSessionKeyRef = useRef(sessionKey)
  const retryPendingRef = useRef(false)

  useEffect(() => {
    if (!enabled || !identity || !bootstrap) {
      previousSessionKeyRef.current = sessionKey
      if (ownsBootstrap) bootstrap?.reset()
      return
    }
    if (
      !ownsBootstrap &&
      previousSessionKeyRef.current !== sessionKey &&
      !identitiesMatch(bootstrap.session?.identity ?? bootstrap.provisionedIdentity, identity)
    ) {
      bootstrap.reset()
    }
    previousSessionKeyRef.current = sessionKey

    return () => {
      if (ownsBootstrap) bootstrap.reset()
    }
  }, [bootstrap, enabled, identity, ownsBootstrap, sessionKey])

  useEffect(() => {
    if (!enabled || !identity || !bootstrap || suppliedBootstrap || activeClientRole === 'never') return
    const instructionRuleSessionKey = identity.taskId ?? identity.sessionId
    const instructionRulePublisher = new InstructionRuleSessionPublisher(instructionScope)
    instructionRulePublisher.activate(instructionRuleSessionKey)

    return () => {
      const currentRelayEntry = getRelayEntry(instructionRuleSessionKey)
      const relayEntry =
        currentRelayEntry?.environmentId === identity.environmentId &&
        currentRelayEntry.sessionId === identity.sessionId &&
        currentRelayEntry.channel === identity.channel
          ? currentRelayEntry
          : undefined
      instructionRulePublisher.deactivate(instructionRuleSessionKey)
      instructionRulePublisher.dispose()
      discardRelayInstructionRulesWhenUnused(instructionRuleSessionKey, relayEntry)
    }
  }, [activeClientRole, bootstrap, enabled, identity, instructionScope, suppliedBootstrap])

  useEffect(() => {
    retryPendingRef.current = false
    if (!enabled || !identity || !bootstrap || !mode) return
    let cancelled = false
    const establish = async () => {
      try {
        const session = await bootstrap.establish({
          mode,
          identity,
          ...(resumptionAttempt ? {resumptionAttempt} : {}),
          ...(awaitIntentAfterLive ? {awaitIntentAfterLive} : {}),
        })
        if (!cancelled) {
          notifySessionEstablished(session)
          setState(current =>
            current.key === sessionKey && current.bootstrap === bootstrap
              ? {...current, session, error: undefined, failure: undefined}
              : current,
          )
        }
      } catch (cause) {
        if (!cancelled) {
          const error = cause instanceof Error ? cause.message : 'The AHP session could not be established.'
          const failure = relayFailureFromError(cause)
          if (relayFailureDisposition(failure).automaticRetry === 'ignore') return
          setState(current =>
            current.key === sessionKey && current.bootstrap === bootstrap ? {...current, error, failure} : current,
          )
        }
      }
    }
    void establish()
    return () => {
      cancelled = true
    }
  }, [attempt, awaitIntentAfterLive, bootstrap, enabled, identity, mode, resumptionAttempt, sessionKey])

  const lifecycle = state.session?.lifecycle
  const lifecycleState = useSyncExternalStore(
    lifecycle?.subscribe ?? subscribeNothing,
    lifecycle?.getState ?? disconnected,
    disconnected,
  )
  const getProgressSnapshot = useCallback(
    () =>
      identity
        ? getRelayConnectionProgress(identity.taskId ?? identity.sessionId, {
            environmentId: identity.environmentId,
            sessionId: identity.sessionId,
            ...(identity.channel ? {channel: identity.channel} : {}),
          })
        : undefined,
    [identity],
  )
  const progress = useSyncExternalStore(subscribeRelayRegistry, getProgressSnapshot, getProgressSnapshot)
  const awaitingIntent = progress?.phase === 'awaiting-intent'
  const connecting =
    !!mode && !state.error && !awaitingIntent && lifecycleState !== 'live' && lifecycleState !== 'failed'
  const connectionPresentation = useConnectionAttemptPresentation(
    connecting,
    `${sessionKey}\u0000${state.attempt}`,
    progress,
    slowWarningEnabled,
  )
  const failure = state.failure ?? (lifecycleState === 'failed' ? state.session?.view.getSnapshot().failure : undefined)
  const canRetry = !failure || relayFailureDisposition(failure).manualRetry
  const connect = useCallback(() => {
    if (!enabled || !bootstrap || retryPendingRef.current || !canRetry) return
    const failed = state.error || state.session?.lifecycle.getState() === 'failed'
    if (!failed) return
    recordRelayRecoveryIntent(taskId, 'manual_retry')
    retryPendingRef.current = true
    bootstrap.reset()
    setState(current => ({
      ...current,
      session: undefined,
      error: undefined,
      failure: undefined,
      attempt: current.attempt + 1,
    }))
  }, [bootstrap, canRetry, enabled, state.error, state.session, taskId])
  return {
    session: state.session,
    error:
      state.error ??
      (lifecycleState === 'failed'
        ? (state.session?.view.getSnapshot().error ?? 'The session disconnected. Retry to connect.')
        : undefined),
    failure,
    connecting,
    live: lifecycleState === 'live',
    reconnecting: !!state.session && lifecycleState === 'reconnecting',
    awaitingIntent,
    progress: awaitingIntent ? undefined : progress,
    showProgress: connectionPresentation.showProgress,
    presentationPhase: connectionPresentation.presentationPhase,
    slow: connectionPresentation.slow,
    canRetry,
    attempt: state.attempt,
    connect,
  }
}
